import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  counterService,
  CreateCounterDto,
} from "../../services/counter.service";
import { storeService, Store } from "../../services/store.service";
import { useAuthStore } from "../../stores/authStore";
import { showToast } from "../../lib/toast";

export function CreateCounterPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { selectedStoreId } = useAuthStore();
  const isEditMode = !!id;
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState<Store[]>([]);
  const [formData, setFormData] = useState<CreateCounterDto>({
    storeId: selectedStoreId || "",
    name: "",
    code: "",
    status: "active",
  });

  useEffect(() => {
    loadStores();
  }, []);

  useEffect(() => {
    if (isEditMode) {
      fetchCounter();
    }
  }, [id]);

  const loadStores = async () => {
    try {
      const data = await storeService.getAll();
      setStores(data || []);
    } catch (err) {
      setStores([]);
    }
  };

  const fetchCounter = async () => {
    try {
      setLoading(true);
      const counter = await counterService.getCounter(id!);
      setFormData({
        storeId: counter.storeId,
        name: counter.name,
        code: counter.code || "",
        status: counter.status,
      });
    } catch (error) {
      console.error("Failed to fetch counter:", error);
      showToast.error("Failed to fetch counter details");
      navigate("/counters");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.storeId) {
      showToast.error("Store is required");
      return;
    }
    if (!formData.name.trim()) {
      showToast.error("Counter name is required");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...formData,
        name: formData.name.trim(),
        code: formData.code?.trim() || undefined,
      };
      if (isEditMode) {
        await counterService.updateCounter(id!, payload);
        showToast.success("Counter updated successfully");
      } else {
        await counterService.createCounter(payload);
        showToast.success("Counter created successfully");
      }
      navigate("/counters");
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message ||
          `Failed to ${isEditMode ? "update" : "create"} counter`,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate("/counters")}
          className="text-blue-600 hover:text-blue-800 mb-2"
        >
          ← Back to Counters
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditMode ? "Edit Counter" : "Add New Counter"}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Store <span className="text-red-500">*</span>
              </label>
              <select
                name="storeId"
                value={formData.storeId}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select store...</option>
                {stores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Counter Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Counter Code
              </label>
              <input
                type="text"
                name="code"
                value={formData.code || ""}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                name="status"
                value={formData.status || "active"}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate("/counters")}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading
              ? "Saving..."
              : isEditMode
                ? "Update Counter"
                : "Create Counter"}
          </button>
        </div>
      </form>
    </div>
  );
}
